import { useState, useMemo } from "react";
import {
  MapPin,
  Wind,
  Clock,
  Zap,
  Activity,
  ChevronRight,
  ChevronLeft,
  Calendar,
  ShieldCheck
} from "lucide-react";
import { AircraftListingDto } from "@/context/AppContext";
import { useNavigation } from "@/context/NavigationContext";
import { useAuth } from "@/context/AuthContext"; // 🟢 Importă hook-ul de autentificare

interface AircraftProductCardProps {
  listing: AircraftListingDto;
}

// 🟢 Funcție utilitară pentru a detecta valori nule, nedefinite sau placeholder-e de tip "N/A"
const isInvalidValue = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  const str = String(value).trim().toLowerCase();
  return str === "" || str === "null" || str === "undefined" || str === "n/a" || str === "na";
};

const AircraftProductCard = ({ listing }: AircraftProductCardProps) => {
  const { navigateTo } = useNavigation();
  const { myAircraftListings } = useAuth(); // 🟢 Extrage listările utilizatorului curent
  const [currentIndex, setCurrentIndex] = useState(0);

  // 🟢 Verifică dacă această listare aparține utilizatorului logat
  const isOwnListing = useMemo(() => {
    if (!myAircraftListings || !listing.listingId) return false;
    return myAircraftListings.some((myListing) => myListing.listingId === listing.listingId);
  }, [myAircraftListings, listing.listingId]);

  const images = (() => {
      // 1. Direct path: Use streaming URLs if returned by your asset manager
      if (listing.mediaUrls && listing.mediaUrls.length > 0) {
        return listing.mediaUrls.filter(url => url && url.trim() !== "");
      }

      // 2. Fallback path: Preserve parsing for older Base64 arrays
      if (listing.media && listing.media.length > 0) {
        return listing.media.map((m) => {
          const data = (m as any).mediaData;
          if (!data) return "";

          if (data.startsWith("iVBOR")) return `data:image/png;base64,${data}`;
          if (data.startsWith("/9j/")) return `data:image/jpeg;base64,${data}`;

          return data.startsWith("data:") ? data : `data:image/jpeg;base64,${data}`;
        }).filter(img => img !== "");
      }

      // 3. Empty Fallback: Dynamic asset placeholder fallback template
      return [`https://via.placeholder.com/400x300?text=${encodeURIComponent(listing.model || "Aircraft")}`];
    })();

  const formatPrice = (price?: number) => {
    if (!price || isInvalidValue(price)) return "LA CERERE";
    return new Intl.NumberFormat("ro-RO", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleCardClick = () => {
    navigateTo("aircraft-details", {
      listingId: listing.listingId,
      aircraftListing: listing,
      seller: isOwnListing ? 1 : 0
    });
  };

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  // Rezolvare label-uri și curățare valori
  const cleanManufacturer = isInvalidValue(listing.manufacturer) ? "" : listing.manufacturer;
  const cleanModel = isInvalidValue(listing.model) ? "Aeronavă" : listing.model;
  const cleanLocation = isInvalidValue(listing.location) ? null : listing.location;

  const typeLabel = isInvalidValue(listing.typeLabel) ? "Aircraft" : listing.typeLabel;
  const stateLabel = isInvalidValue(listing.stateLabel) ? null : listing.stateLabel;
  const productionYear = isInvalidValue(listing.productionYear) ? null : listing.productionYear;
  const flightTime = isInvalidValue(listing.flightTime) ? null : listing.flightTime;

  return (
    <div
      onClick={handleCardClick}
      className={`group relative bg-[#0F172A]/80 backdrop-blur-xl border rounded-3xl overflow-hidden cursor-pointer hover:border-orange-500/50 transition-all duration-300 shadow-2xl h-full flex flex-col ${
        isOwnListing ? "border-dashed border-slate-700 opacity-95" : "border-slate-800"
      }`}
    >
      {/* IMAGE SECTION WITH HUD OVERLAY */}
      <div className="relative h-56 overflow-hidden shrink-0">
        <img
          src={images[currentIndex]}
          alt={cleanModel}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          onError={(e) => {
            (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300?text=Image+Unavailable';
          }}
        />

        {/* Top Badges */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-2 z-10 max-w-[calc(100%-24px)]">
          {/* 🟢 Adăugat: Badge pentru listarea proprie, la fel ca la piese */}
          {isOwnListing && (
            <span className="bg-slate-900/90 backdrop-blur-md border border-orange-500/30 text-orange-400 text-[9px] font-black px-2 py-1 rounded-md uppercase tracking-widest shadow-lg animate-pulse">
              Propria listare
            </span>
          )}
          <span className="bg-orange-600 text-white text-[9px] font-black px-2 py-1 rounded-md uppercase tracking-widest shadow-lg">
            {typeLabel}
          </span>
          {stateLabel && (
            <span className="bg-slate-900/90 backdrop-blur-md border border-slate-700 text-white text-[9px] font-black px-2 py-1 rounded-md uppercase tracking-widest shadow-lg">
              {stateLabel}
            </span>
          )}
        </div>

        {/* Carousel Controls */}
        {images.length > 1 && (
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-2 opacity-0 group-hover:opacity-100 transition-opacity z-20">
            <button onClick={prevImage} className="p-1.5 bg-slate-900/80 rounded-full text-white hover:bg-orange-600 transition-colors">
              <ChevronLeft size={16} />
            </button>
            <button onClick={nextImage} className="p-1.5 bg-slate-900/80 rounded-full text-white hover:bg-orange-600 transition-colors">
              <ChevronRight size={16} />
            </button>
          </div>
        )}

        {/* Carousel Indicators */}
        {images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1 z-10">
            {images.map((_, idx) => (
              <span
                key={idx}
                className={`h-1 rounded-full transition-all ${currentIndex === idx ? "bg-orange-500 w-4" : "bg-white/30 w-1"}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* CONTENT SECTION */}
      <div className="p-5 flex flex-col flex-1 justify-between">
        <div className="space-y-4">
          {/* Title & Location */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Activity size={14} className="text-orange-500 animate-pulse" />
              <h3 className="text-lg font-black text-white tracking-tight uppercase truncate">
                {cleanManufacturer ? (
                  <>
                    {cleanManufacturer} <span className="text-slate-500">{cleanModel}</span>
                  </>
                ) : (
                  cleanModel
                )}
              </h3>
            </div>
            {/* 🟢 MODIFICAT: Ascunde complet locația dacă este nulă sau N/A */}
            {cleanLocation && (
              <div className="flex items-center gap-1 text-slate-500">
                <MapPin size={12} />
                <span className="text-[11px] font-bold uppercase tracking-wider truncate">{cleanLocation}</span>
              </div>
            )}
          </div>

          {/* PERFORMANCE/SPECS HUD GRID */}
          <div className="grid grid-cols-3 gap-2 border-y border-slate-800/50 py-3">
            <div className="text-center">
              <Calendar size={14} className="mx-auto mb-1 text-slate-400" />
              {/* 🟢 MODIFICAT: Verificare valoare nulă */}
              <p className="text-[10px] font-black text-white">{productionYear || "-"}</p>
              <p className="text-[8px] text-slate-500 font-bold uppercase">Year</p>
            </div>
            <div className="text-center border-x border-slate-800/50">
              <Clock size={14} className="mx-auto mb-1 text-slate-400" />
              {/* 🟢 MODIFICAT: Verificare valoare nulă */}
              <p className="text-[10px] font-black text-white">
                {flightTime ? `${flightTime} H` : "-"}
              </p>
              <p className="text-[8px] text-slate-500 font-bold uppercase">Total Time</p>
            </div>
            <div className="text-center">
              <ShieldCheck size={14} className="mx-auto mb-1 text-slate-400" />
              {/* 🟢 MODIFICAT: Verificare valoare nulă */}
              <p className="text-[10px] font-black text-white truncate px-1 uppercase">
                {stateLabel ? stateLabel.split(' ')[0] : "-"}
              </p>
              <p className="text-[8px] text-slate-500 font-bold uppercase">Status</p>
            </div>
          </div>
        </div>

        {/* PRICING FOOTER */}
        <div className="flex items-end justify-between mt-4">
          <div>
            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-[0.2em]">Listing Price</p>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-black text-white tracking-tighter">
                {formatPrice(listing.grossValue)}
              </span>
            </div>
          </div>
          <div className="text-right">
            {listing.negotiable && (
              <span className="text-[9px] font-mono font-bold text-emerald-400 border border-emerald-400/20 px-2 py-1 rounded bg-emerald-400/5">
                NEGOCIABIL
              </span>
            )}
          </div>
        </div>
      </div>

      {/* DECORATIVE LINE - Orange for Sales */}
      <div className="absolute bottom-0 left-0 h-1 bg-orange-600 transition-all duration-300 w-0 group-hover:w-full" />
    </div>
  );
};

export default AircraftProductCard;